import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import './remotePwaSupport.css';

const SURFACE_CONFIG = {
  remote: {
    label: 'Phone Remote',
    manifestPath: '/manifest-remote.json',
    themeColor: '#0f766e',
    appleTitle: 'Phone Remote',
    iosHint: 'Tap Share, then Add to Home Screen for one-tap venue control.',
  },
  console: {
    label: 'Console Remote',
    manifestPath: '/manifest-console.json',
    themeColor: '#b45309',
    appleTitle: 'Console Remote',
    iosHint: 'Tap Share, then Add to Home Screen to launch the console as a full-screen app.',
  },
};

let remoteServiceWorkerRegistration = null;
const PROMPT_DISMISSAL_VERSION = 'v2';

const isStandaloneMode = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
};

const isIosSafari = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const isWebkit = /webkit/i.test(ua);
  const isOtherBrowser = /crios|fxios|edgios/i.test(ua);
  return isIos && isWebkit && !isOtherBrowser;
};

const buildManifestPath = (surface, fallbackPath) => {
  if (typeof window === 'undefined') return fallbackPath;
  const claimToken = new URLSearchParams(window.location.search).get('pwaClaim');
  if (!claimToken) return fallbackPath;
  const params = new URLSearchParams({ surface, pwaClaim: claimToken });
  return `/api/devices/remote-pwa-manifest?${params.toString()}`;
};

const ensureRemoteServiceWorker = async () => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  if (!remoteServiceWorkerRegistration) {
    remoteServiceWorkerRegistration = navigator.serviceWorker.register('/remote-service-worker.js');
  }
  return remoteServiceWorkerRegistration;
};

export default function RemotePwaSupport({ surface = 'remote', promptTargetId = '', children }) {
  const config = SURFACE_CONFIG[surface] || SURFACE_CONFIG.remote;
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [standalone, setStandalone] = useState(() => isStandaloneMode());
  const [promptTarget, setPromptTarget] = useState(null);
  const dismissedStorageKey = `remotePwaPromptDismissed:${PROMPT_DISMISSAL_VERSION}:${surface}`;
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(dismissedStorageKey) === '1';
  });

  useEffect(() => {
    setDismissed(window.localStorage.getItem(dismissedStorageKey) === '1');
  }, [dismissedStorageKey]);

  useEffect(() => {
    const manifestLink = document.querySelector('#app-manifest');
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const previousManifest = manifestLink?.getAttribute('href') || '';
    const previousTheme = themeMeta?.getAttribute('content') || '';
    const previousAppleTitle = appleTitleMeta?.getAttribute('content') || '';
    const manifestPath = buildManifestPath(surface, config.manifestPath);

    if (manifestLink) {
      manifestLink.setAttribute('href', manifestPath);
    }
    if (themeMeta) {
      themeMeta.setAttribute('content', config.themeColor);
    }
    if (appleTitleMeta) {
      appleTitleMeta.setAttribute('content', config.appleTitle);
    }

    return () => {
      if (manifestLink && previousManifest) {
        manifestLink.setAttribute('href', previousManifest);
      }
      if (themeMeta && previousTheme) {
        themeMeta.setAttribute('content', previousTheme);
      }
      if (appleTitleMeta && previousAppleTitle) {
        appleTitleMeta.setAttribute('content', previousAppleTitle);
      }
    };
  }, [config.appleTitle, config.manifestPath, config.themeColor, surface]);

  useEffect(() => {
    ensureRemoteServiceWorker().catch((error) => {
      console.error('Failed to register remote service worker:', error);
    });
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleInstalled = () => {
      setDeferredPrompt(null);
      setStandalone(true);
      setDismissed(true);
      window.localStorage.setItem(dismissedStorageKey, '1');
    };

    const handleVisibility = () => {
      setStandalone(isStandaloneMode());
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [dismissedStorageKey]);

  const promptMode = useMemo(() => {
    if (standalone || dismissed) return null;
    if (deferredPrompt) return 'install';
    if (isIosSafari()) return 'ios';
    return null;
  }, [deferredPrompt, dismissed, standalone]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice.catch(() => null);
    if (result?.outcome === 'accepted') {
      setDismissed(true);
      window.localStorage.setItem(dismissedStorageKey, '1');
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setDismissed(true);
    window.localStorage.setItem(dismissedStorageKey, '1');
  };

  useEffect(() => {
    if (!promptMode || !promptTargetId || typeof document === 'undefined') {
      setPromptTarget(null);
      return undefined;
    }

    let frameId = 0;
    const resolveTarget = () => {
      setPromptTarget(document.getElementById(promptTargetId));
    };
    frameId = window.requestAnimationFrame(resolveTarget);
    return () => window.cancelAnimationFrame(frameId);
  }, [promptMode, promptTargetId]);

  const prompt = promptMode ? (
    <aside
      className={`remote-pwa-prompt${promptTarget ? ' remote-pwa-prompt--inline' : ''}`}
      role="dialog"
      aria-live="polite"
      aria-label={`${config.label} install prompt`}
    >
      <div className="remote-pwa-prompt__content">
        <div className="remote-pwa-prompt__eyebrow">Add to Home Screen</div>
        <strong className="remote-pwa-prompt__title">{config.label}</strong>
        <p className="remote-pwa-prompt__copy">
          {promptMode === 'install'
            ? 'Install this remote for full-screen controls and one-tap access from this device.'
            : config.iosHint}
        </p>
      </div>
      <div className="remote-pwa-prompt__actions">
        {promptMode === 'install' ? (
          <button type="button" className="remote-pwa-prompt__primary" onClick={handleInstall}>
            Install
          </button>
        ) : null}
        <button type="button" className="remote-pwa-prompt__ghost" onClick={handleDismiss}>
          {promptMode === 'install' ? 'Later' : 'Hide'}
        </button>
      </div>
    </aside>
  ) : null;

  return (
    <>
      {children}
      {promptTarget && prompt ? ReactDOM.createPortal(prompt, promptTarget) : prompt}
    </>
  );
}
